import { Suspense, useState } from 'react';

import { IconAngleLeft } from '@iwoca/icons';
import { Button, Text } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { ErrorMessage } from './ErrorMessage';
import { ResidentialAddress } from './ResidentialAddress';
import { usePatchCreditCard } from './usePatchCreditCard';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../../pageStructure/mainContentPending';
import { Radio } from '../../../../ui/RadioInput/RadioInput';
import { capitaliseWord } from '../../../helpers';
import CreditCardLetter from '../../../images/credit-card-letter.png';
import { Card as CreditCard } from '../../types';
import { useGetCreditCardDashboardData } from '../../useGetCreditCardDashboardData';

type ReplacementReason = 'lost' | 'stolen' | 'damaged' | null;

const CreditCardLetterImg = () => (
    <div className="my-l">
        <img
            src={CreditCardLetter}
            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
            alt="Credit Card in Letter"
            className="h-[100px] w-[100px]"
        />
    </div>
);

const BackButton = ({ onBack }: { onBack: () => void }) => (
    <div className="mb-xl">
        <button
            className="flex cursor-pointer items-center gap-s border-0 bg-transparent p-0"
            onClick={onBack}
        >
            <IconAngleLeft
                width={24}
                height={24}
                className="font-bold text-primary-40"
            />
            <Text className="text-l font-bold text-primary-40">
                <FormattedMessage
                    id="_.credit_card.dashboard.replace_card.back_button"
                    defaultMessage="Back"
                />
            </Text>
        </button>
    </div>
);

const RadioOption = ({
    selectedReason,
    setSelectedReason,
    reason,
    title,
    description
}: {
    selectedReason: ReplacementReason;
    setSelectedReason: (selectedReason: ReplacementReason) => void;
    reason: 'lost' | 'stolen' | 'damaged';
    title: React.ReactNode;
    description: React.ReactNode;
}) => (
    <label
        htmlFor={reason}
        className="flex cursor-pointer flex-row-reverse items-start justify-between gap-s rounded-m border border-secondary-90 p-l"
    >
        <Radio
            id={reason}
            name="replacementReason"
            value={reason}
            checked={selectedReason === reason}
            onChange={() => setSelectedReason(reason)}
        />
        <div className="flex flex-1 flex-col gap-xs">
            <Text className="m-0 text-m font-med text-primary-10">{title}</Text>
            <Text className="m-0 text-s text-primary-10">{description}</Text>
        </div>
    </label>
);

const SelectionView = ({
    selectedReason,
    setSelectedReason,
    setSelectionConfirmed,
    card
}: {
    selectedReason: ReplacementReason;
    setSelectedReason: (content: ReplacementReason) => void;
    setSelectionConfirmed: (value: boolean) => void;
    card: CreditCard;
}) => (
    <div className="flex flex-col gap-l">
        <CreditCardLetterImg />
        <Text className="m-0 text-2xl font-bold max-sm:text-l">
            <FormattedMessage
                id="_.credit_card.dashboard.replace_card.title"
                defaultMessage="Replace card ending -{lastFourDigits}"
                values={{ lastFourDigits: card.last_four_digits }}
            />
        </Text>

        <Text className="max-sm:text-sm text-m text-primary-10">
            <FormattedMessage
                id="_.credit_card.dashboard.replace_card.description"
                defaultMessage="Order a replacement card If you've damaged your card or it's been lost or stolen."
            />
        </Text>

        <div className="flex flex-col gap-m">
            <RadioOption
                selectedReason={selectedReason}
                setSelectedReason={setSelectedReason}
                reason="lost"
                title={
                    <FormattedMessage
                        id="_.credit_card.dashboard.replace_card.radio_option.lost_title"
                        defaultMessage="Lost card"
                    />
                }
                description={
                    <FormattedMessage
                        id="_.credit_card.dashboard.replace_card.radio_option.lost_description"
                        defaultMessage="Immediately cancel this card and issue a new card with a new PIN which will be delivered to you."
                    />
                }
            />
            <RadioOption
                selectedReason={selectedReason}
                setSelectedReason={setSelectedReason}
                reason="stolen"
                title={
                    <FormattedMessage
                        id="_.credit_card.dashboard.replace_card.radio_option.stolen_title"
                        defaultMessage="Stolen card"
                    />
                }
                description={
                    <FormattedMessage
                        id="_.credit_card.dashboard.replace_card.radio_option.stolen_description"
                        defaultMessage="Immediately cancel this card and issue a new card with a new PIN which will be delivered to you."
                    />
                }
            />
            <RadioOption
                selectedReason={selectedReason}
                setSelectedReason={setSelectedReason}
                reason="damaged"
                title={
                    <FormattedMessage
                        id="_.credit_card.dashboard.replace_card.radio_option.damaged_title"
                        defaultMessage="Damaged card"
                    />
                }
                description={
                    <FormattedMessage
                        id="_.credit_card.dashboard.replace_card.radio_option.damaged_description"
                        defaultMessage="Report your card as damaged and re-issue you a new physical card with the same details and PIN which will be delivered to you."
                    />
                }
            />
        </div>

        <Button
            variant="primary"
            disabled={!selectedReason}
            className="w-full"
            onClick={() => setSelectionConfirmed(true)}
        >
            <FormattedMessage
                id="_.credit_card.dashboard.replace_card.confirm_button"
                defaultMessage="Confirm selection"
            />
        </Button>
    </div>
);

const ActionView = ({
    onClose,
    onBack,
    card,
    reason
}: {
    onClose: () => void;
    onBack: () => void;
    card: CreditCard;
    reason: 'lost' | 'stolen' | 'damaged';
}) => {
    const [isConfirmed, setIsConfirmed] = useState(false);
    const {
        mutate: performCardAction,
        isLoading,
        isError
    } = usePatchCreditCard(card.enfuce_card_id);

    const handleConfirm = () => {
        performCardAction(
            { action: `report_${reason}` },
            { onSuccess: () => setIsConfirmed(true) }
        );
    };

    const isDamaged = reason === 'damaged';

    const confirmedDescriptionMessage = isDamaged ? (
        <FormattedMessage
            id="_.credit_card.dashboard.replace_card.damaged_confirmed_description"
            defaultMessage="Your replacement physical card is on its way to you. It should be delivered within the next 5 working days."
        />
    ) : (
        <FormattedMessage
            id="_.credit_card.dashboard.replace_card.lost_stolen_confirmed_description"
            defaultMessage="Your new physical card and PIN are on their way to you. They should be delivered within the next 5 working days."
        />
    );

    const descriptionMessage = isDamaged ? (
        <FormattedMessage
            id="_.credit_card.dashboard.replace_card.damaged_description"
            defaultMessage="Issue a replacement physical card with the same details and PIN which will be delivered to you. You can still use the card's details to make purchases."
        />
    ) : (
        <FormattedMessage
            id="_.credit_card.dashboard.replace_card.lost_stolen_description"
            defaultMessage="Cancel the card immediately and issue a new card and PIN which will be delivered to you."
        />
    );

    const addressInfoMessage = isDamaged ? (
        <FormattedMessage
            id="_.credit_card.dashboard.replace_card.damaged_address_info"
            defaultMessage="Your new physical card and PIN will be sent separately to the following address:"
        />
    ) : (
        <FormattedMessage
            id="_.credit_card.dashboard.replace_card.lost_stolen_address_info"
            defaultMessage="Your new physical card and PIN will be sent separately to the following address:"
        />
    );

    if (isConfirmed) {
        return (
            <div className="flex min-h-[600px] flex-col justify-center gap-l">
                <Text className="m-0 text-2xl font-bold max-sm:text-l">
                    <FormattedMessage
                        id="_.credit_card.dashboard.replace_card.lost_stolen_confirmed_title"
                        defaultMessage="That's sorted."
                    />
                </Text>
                <Text className="max-sm:text-sm text-l text-primary-10">
                    {confirmedDescriptionMessage}
                </Text>
                <Button onClick={onClose}>
                    <FormattedMessage
                        id="_.credit_card.dashboard.replace_card.return_to_dashboard_button"
                        defaultMessage="Return to dashboard"
                    />
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-m">
            <BackButton onBack={onBack} />
            <CreditCardLetterImg />
            <Text className="m-0 text-2xl font-bold max-sm:text-l">
                <FormattedMessage
                    id="_.credit_card.dashboard.replace_card.lost_stolen_view_title"
                    defaultMessage="{reason} card ending -{lastFourDigits}"
                    values={{
                        lastFourDigits: card.last_four_digits,
                        reason: capitaliseWord(reason)
                    }}
                />
            </Text>
            <Text className="max-sm:text-sm text-l text-primary-10">
                {descriptionMessage}
            </Text>
            <Text className="max-sm:text-sm mt-l text-m text-primary-10">
                {addressInfoMessage}
            </Text>
            <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                <Suspense fallback={<MainContentPending />}>
                    <ResidentialAddress />
                </Suspense>
            </ErrorBoundary>
            <Button
                className="my-l"
                onClick={handleConfirm}
                disabled={isLoading}
            >
                <FormattedMessage
                    id="_.credit_card.dashboard.replace_card.lost_stolen_confirm_button"
                    defaultMessage="Confirm and order new card"
                />
            </Button>
            {isError && <ErrorMessage />}
        </div>
    );
};

export const ReplaceCard = ({ onClose }: { onClose: () => void }) => {
    const [selectedReason, setSelectedReason] =
        useState<ReplacementReason>(null);
    const [isSelectionConfirmed, setIsSelectionConfirmed] = useState(false);
    const { activeCard } = useGetCreditCardDashboardData();

    const handleBackToSelection = () => setIsSelectionConfirmed(false);

    if (!isSelectionConfirmed || !selectedReason) {
        return (
            <SelectionView
                selectedReason={selectedReason}
                setSelectedReason={setSelectedReason}
                setSelectionConfirmed={setIsSelectionConfirmed}
                card={activeCard!}
            />
        );
    }

    const viewProps = {
        onClose,
        onBack: handleBackToSelection,
        card: activeCard!
    };

    return <ActionView {...viewProps} reason={selectedReason} />;
};
